import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FaShieldAlt, FaSync, FaUserSlash, FaCheck } from 'react-icons/fa';
import { catalogNodeService } from '../../../../services/apiService';

export default function SellerReviewQueuePanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await catalogNodeService.listSellerReviewQueue();
      setRows(res?.data || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load review queue');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const clearSeller = async (sellerId) => {
    try {
      setActingId(sellerId);
      await catalogNodeService.clearSellerReviewQueue(sellerId, 'Cleared by admin after review');
      toast.success('Seller cleared from review queue');
      await load();
    } catch (err) {
      toast.error(err.message || 'Action failed');
    } finally {
      setActingId(null);
    }
  };

  const suspendSeller = async (sellerId) => {
    const reason = window.prompt('Suspension reason (optional):', 'Repeated price fraud blocks');
    if (reason === null) return;
    try {
      setActingId(sellerId);
      await catalogNodeService.suspendSellerFromReview(sellerId, { days: 7, reason });
      toast.success('Seller suspended for 7 days');
      await load();
    } catch (err) {
      toast.error(err.message || 'Suspend failed');
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <FaShieldAlt className="text-amber-600" />
            Seller review queue
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Sellers with 3+ price hard blocks in 30 days (listing creation or edit).
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm"
        >
          <FaSync className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-6 py-10 text-center">
          <p className="font-semibold text-emerald-900">No sellers in review queue</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Seller</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Flagged</th>
                <th className="px-4 py-3">Recent blocks</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={String(row.sellerId)} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-medium">{row.sellerName}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {row.email || '—'}
                    <br />
                    {row.phone || ''}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {row.reviewQueueEnteredAt
                      ? new Date(row.reviewQueueEnteredAt).toLocaleString('en-IN')
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs max-w-xs">
                    {(row.recentPriceHardBlocks || []).map((b, i) => (
                      <div key={i} className="truncate text-red-700">
                        {b.reason || 'Price block'}
                      </div>
                    ))}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={actingId === row.sellerId}
                        onClick={() => clearSeller(row.sellerId)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs"
                      >
                        <FaCheck /> Clear
                      </button>
                      <button
                        type="button"
                        disabled={actingId === row.sellerId}
                        onClick={() => suspendSeller(row.sellerId)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-300 text-red-700 text-xs"
                      >
                        <FaUserSlash /> Suspend
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
